import { Fragment } from 'react';

import VendorSupportEmail from '~/components/common/vendors/VendorSupportEmail';

import { ZolaPrivacyLink, ZolaTermsLink, ZolaVendorCreditsTermsLink } from './TermsHelper';

export const CPP_ANCHOR = 'connection-protection-policy' as const;
export const PLACEMENT_ANCHOR = 'featured-vendor-program' as const;

export type TermsSection = {
  // Title is optional, but if present is rendered as an h2
  title?: string;

  // A Paragraph of the terms
  content: React.ReactNode;

  // Lets you apply an anchor to any section.  You may not need this, you may just want to push it into the content
  id?: string;
};

type TermsAndConditionsWithoutDateIKnowWhatImDoingAndIAcceptTheConsequences = {
  lastUpdated: undefined;
};
type TermsAndConditionsWithDate = {
  lastUpdated: string;
};

type TermsTitleAndSection = {
  // Title of the page (render as h1)
  title: string;

  // Drop subTitle & Content and just make it a section
  sections: TermsSection[];
};

type StandardTermsAndConditions = TermsTitleAndSection & TermsAndConditionsWithDate;
type DangerousTermsAndConditions = TermsTitleAndSection &
  TermsAndConditionsWithoutDateIKnowWhatImDoingAndIAcceptTheConsequences;

export type TermsAndConditions = StandardTermsAndConditions | DangerousTermsAndConditions;

export const VENDOR_PLANS_TERMS: DangerousTermsAndConditions = {
  title: 'Vendor Plans Terms & Conditions',
  sections: [
    {
      content: (
        <Fragment>
          These Vendor Plans Terms and Conditions govern your receipt and use of Vendor Plans. By
          using our Vendor Plans, you agree to be bound by these Vendor Plans Terms and Conditions,
          which are governed by and hereby incorporate Zola’s <ZolaTermsLink />. Any terms not
          defined herein shall have the meanings ascribed to them in our <ZolaTermsLink />. To the
          extent any conflict exists between the terms set forth in these Vendor Plans Terms and
          Conditions and the terms set forth in Zola’s <ZolaTermsLink />, these Vendor Plans Terms
          and Conditions shall control.
        </Fragment>
      ),
    },
    {
      title: 'Vendor Credits & Vendor Plans',
      content: `Vendor Credits are virtual credits that are made available to Vendors by Zola for use in 
      connecting with a Zola couple or other Zola user (a “Lead”) that has sent an inquiry to a Vendor 
      storefront on Vendor Marketplace (an “Inquiry”). After receiving an Inquiry from a Lead, a Vendor can 
      “Connect” with the Lead by applying Vendor Credits to send a message (“Initial Connection Message”) to 
      the Lead via Vendor Marketplace (a “Connection”). Vendor Credits may be issued to Vendors by Zola free of 
      charge under certain circumstances, or Vendors may be required to purchase Vendor Credits on Vendor 
      Marketplace by buying a vendor plan (“Vendor Plan”). Zola offers two types of Vendor Plans for purchase: 
      (1) our “Pay as You Go Plan,” which contains a fixed number of Vendor Credits that you can use to Connect 
      with a Lead at any time for the amount of Vendor Credits required for the applicable Connection, and (2) 
      our “Monthly Plan” or “Annual Plan” (also referred to as an “Unlimited Plan” or Unlimited Plus Plan,” and 
      all such plans collectively for purposes of these Vendor Plans Terms and Conditions, the “Monthly Plans”) 
      that will enable you to Connect with an unlimited number of Leads during the amount of time specified for 
      such Vendor Plan at the time of purchase(e.g., three months or twelve months). The decision to charge for 
      Vendor Credits and Vendor Plans will be taken by Zola in its sole discretion. Pricing for Vendor Credits 
      and Vendor Plans will be determined by Zola in its sole discretion and will be displayed within your 
      Vendor account.`,
    },
    {
      title: 'Application of Vendor Credits',
      content: `The amount of Vendor Credits required for a Connection will be determined by Zola in its sole 
      discretion and may be based on the type of goods or services offered by the applicable Vendor and/or 
      other factors relevant to the applicable Lead. Vendor Credits and Vendor Plans are stored in each 
      Vendor’s Vendor account at the storefront level and may be applied only by Vendors on Vendor Marketplace. 
      Unless otherwise determined by Zola in its sole discretion, Vendor Credits and Vendor Plans cannot be 
      applied or otherwise used for any of the following: cash; the purchase of Zola Stored Value or any 
      physical product, experience or digital Zola gift card that Zola sells; items purchased from another 
      website; or honeymoon funds or other cash gifts.`,
    },
    {
      title: 'Expiration and Refunds',
      content: (
        <Fragment>
          Vendor Credits issued by Zola free of charge expire 12 months following the date on which
          they are deposited in a Vendor’s account. Vendor Credits purchased as part of a Pay as You
          Go Plan will never expire (subject to applicable state laws on escheatment). Monthly Plans
          can be used for the amount of time specified for the applicable Monthly Plan at the time
          of purchase, beginning on the date on which such Monthly Plan is delivered to a Vendor’s
          account. Monthly Plans automatically renew for the time period specified at the time of
          purchase at the rate in effect at the time of such renewal and will continue to do so
          until you choose to cancel. You can cancel your Monthly Plan at any time by going to “
          <a href="/inspire/vendors/your-plans" target="_blank" rel="noopener noreferrer">
            Your Plans
          </a>
          ” within your Vendor account. Vendor Credits and Vendor Plans are refundable only in
          certain circumstances as determined by Zola in its sole discretion. All refund requests
          must be made by emailing <VendorSupportEmail /> within 30 days following purchase of the
          applicable Vendor Plan. Only full, unused Vendor Plans are eligible for a refund; partial
          refunds will not be issued. Vendor Credits and Vendor Plans (and any related promotional
          codes or coupons) have no cash value and are non-transferable (other than as between
          storefronts within the same Vendor account). If you are a Vendor and you contact Zola to
          exercise your right to delete your personal information, including your Vendor account,
          under applicable privacy law, any Vendor Credits or Vendor Plans remaining in your Vendor
          account will no longer be redeemable.
        </Fragment>
      ),
    },
    {
      title: 'Connection Protection Policy',
      content: (
        <Fragment>
          If you Connect with a Lead and that Lead does not reply to your Initial Connection Message
          within 14 days following the Connection, then, (a) if you used Vendor Credits purchased as
          part of a Pay as You Go Plan to make the Connection, you can request that such Vendor
          Credits be returned to your Vendor account, or (b) if you used Vendor Credits purchased as
          part of a Monthly Plan to make the Connection, then you can request to have two (2) Vendor
          Credits issued to your Vendor account as a goodwill offering (“Goodwill Credits”). To
          receive Vendor Credits back or to be issued Goodwill Credits under this Connection
          Protection Policy, you must email <VendorSupportEmail /> within 60 days following the
          applicable Connection and request a return of the applicable Vendor Credits or an issuance
          of Goodwill Credits, as applicable, including in your email the name of the Lead with whom
          you Connected and the date on which they sent their Inquiry. Zola reserves the right to
          decline requests for the return of Vendor Credits or the issuance of Goodwill Credits in
          its sole discretion, including, without limitation, if any of the following apply: (1) the
          applicable Lead replied to your Initial Connection Message off of the Vendor Marketplace
          platform within 14 days following your Initial Connection Message, (2) if you ask, induce,
          or otherwise encourage applicable Lead not to reply to your Initial Connection Message
          with the intention of subverting this Connection Protection Policy, or (3) if you are in
          violation of this Connection Protection Policy, these Vendor Plans Terms and Conditions,
          our <ZolaTermsLink /> or any other applicable terms and conditions. For clarity, this
          Connection Protection Policy applies only to the return of the applicable Vendor Credits
          to your Vendor account or the issuance of Goodwill Credits to your Vendor account, as
          applicable; it does not entitle you to a refund for the purchase of your Vendor Credits.
        </Fragment>
      ),
      id: CPP_ANCHOR,
    },
  ],
  lastUpdated: undefined,
};

export const FEATURED_VENDORS_TERMS: StandardTermsAndConditions = {
  title: 'Featured Vendor Program Terms and Conditions',
  sections: [
    {
      id: PLACEMENT_ANCHOR,
      content: (
        <p>
          Zola offers a featured vendor program to promote vendor listings. These Featured Vendor
          Program Terms and Conditions explain your rights and responsibilities when using Zola’s
          featured vendor program. By using our featured vendor program, you agree to be bound by
          these Featured Vendor Program Terms and Conditions, which are governed by and hereby
          incorporate Zola’s <ZolaTermsLink />, and our <ZolaPrivacyLink />. Any terms not defined
          herein shall have the meanings ascribed to them in our Terms of Use. To the extent any
          conflict exists between the terms set forth in these Featured Vendor Program Terms and
          Conditions and the terms set forth in Zola’s Terms of Use, these Featured Vendor Program
          Terms and Conditions shall control. These Featured Vendor Program Terms and Conditions may
          change over time, per the terms of Zola’s Terms of Use. By continuing to use Zola’s
          services you agree to those changes.
        </p>
      ),
    },
    {
      content: (
        <p>
          Vendors can purchase featured placement in vendor listing search results for a specified
          market (e.g. New York City, Westchester County or North New Jersey) for a selected period
          of time (e.g. January – March 2024) on Zola by going to the “Boost listing” tab on your
          Zola vendor account and choosing the markets you want to be featured in and the time
          period you would like to be featured during. With a featured spot, your listing shows up
          on the first page of search results for the chosen market. Your listing will go live in
          the featured spot at the start of the time period you have chosen. If there is still
          availability for your chosen market after the set period has started, you may purchase a
          featured spot for appearance in the featured spot for the remainder of the period.
          Geographic markets and specific placement on the first page of search results are
          determined by Zola, in its sole discretion.
        </p>
      ),
    },
    {
      content: (
        <p>
          If you make any edits to your Zola listing during your featured time period, your featured
          listing will be updated automatically to reflect those changes, although those updates may
          not be instantaneous.
        </p>
      ),
    },
    {
      content: (
        <p>
          You may not pause a featured placement once your featured listing goes live, and there are
          no refunds once your featured vendor placement has been purchased.
        </p>
      ),
    },
    {
      content: (
        <p>
          Zola reserves the right to change the terms of the featured vendor program at any time,
          including fees, addition or removal of platforms, or end the program (in whole or part) at
          any time. We will notify you of major changes. Further, from time to time Zola may test or
          experiment with advertising products and the advertising services offered by Zola, as well
          as the look and feel of any type of advertisement, may change as our programs and
          platforms evolve.
        </p>
      ),
    },
    {
      content: (
        <p>
          Using the feature vendor program through Zola does not constitute an obligation on Zola’s
          part to display an ad or promote your listings or shops. Zola cannot guarantee that a
          featured vendor listing will be clicked, and we cannot guarantee a successful transaction
          between you and other Zola users. YOU ACKNOWLEDGE AND AGREE THAT THE FEATURED VENDOR
          PROGRAM IS PROVIDED TO YOU ON AN “AS IS”, “WITH ALL FAULTS” AND “AS AVAILABLE” BASIS. ZOLA
          MAKES NO WARRANTIES, EITHER EXPRESS OR IMPLIED, ABOUT THE FEATURED VENDOR PROGRAM AND
          EXPRESSLY DISCLAIMS THE WARRANTY OF MERCHANTABILITY AND WARRANTY OF FITNESS FOR A
          PARTICULAR PURPOSE. FURTHERMORE, TO THE FULLEST EXTENT PERMITTED BY LAW ZOLA SPECIFICALLY
          DISCLAIMS ALL WARRANTIES AND GUARANTEES REGARDING: (I) THE PERFORMANCE, QUALITY AND
          RESULTS OF THE FEATURED VENDOR PROGRAM, INCLUDING ANY CLICK RATES, CONVERSIONS, PACING AND
          ANY USER-GENERATED CONTENT THAT APPEARS ON THE SITE OR IN YOUR LISTING, AND (II) THE
          PLACEMENT, CONTENT, PROMOTIONAL VALUE, QUALITY, TIMING, OR NUMBER OF IMPRESSIONS. ZOLA
          SHALL NOT BE LIABLE FOR NON-PERFORMANCE DUE TO CAUSES BEYOND ITS REASONABLE CONTROL.{' '}
        </p>
      ),
    },
    {
      content: (
        <p>
          Featured listings may not include content that infringes upon or violates the rights of
          any third party, including copyright, trademark, privacy, publicity, or other personal or
          proprietary rights. Please see our <ZolaTermsLink /> for more information. You are
          responsible for ensuring that your listing complies with all applicable laws and
          regulations. Zola reserves the right to reject or remove any listing for any reason, in
          our sole discretion, as described in our Terms of Use.
        </p>
      ),
    },
  ],
  lastUpdated: 'Last Updated: November 27, 2023',
};

export const VENDOR_REVIEW_TERMS: StandardTermsAndConditions = {
  title: 'Vendor Review Terms and Conditions',
  sections: [
    {
      content: (
        <Fragment>
          <p>
            These Vendor Review Terms and Conditions govern your conduct associated with the
            submission of Vendor Reviews, as such review service and functionality are offered by
            Zola, Inc. (“Zola”). All capitalized terms used but not defined in these Vendor Review
            Terms and Conditions shall have the meanings set forth in Zola’s <ZolaTermsLink />. To
            the extent any conflict exists between the terms set forth in these Vendor Review Terms
            and Conditions and the terms set forth in Zola’s <ZolaTermsLink />, these Vendor Review
            Terms and Conditions shall control.
          </p>
          <p>
            Vendor Reviews consist of opinions and information submitted by users about Vendors as
            well the responses submitted by such Vendors. You may submit a Vendor Review if and only
            if you meet the qualifications listed below. If you are a Vendor and you are asking
            clients or fellow wedding professionals to submit Vendor Reviews regarding your products
            or services, you are responsible for ensuring that such individuals meet the below
            qualifications. You may be required to verify your identity in order to submit your
            Vendor Review.
          </p>
          <p>
            <ul>
              <li>
                <p>
                  <strong>Clients:</strong> You were a client of the applicable Vendor, the
                  Vendor-client relationship was entered into pursuant to a bona fide, signed
                  contract and the event for which you engaged the applicable Vendor occurred within
                  four (4) years prior to the date on which you are submitting the Vendor Review;
                  provided, that, for purposes of these Vendor Review Terms and Conditions, you will
                  also be considered a “client” of the applicable Vendor if another individual
                  entered into such contract on your behalf and you were a principal beneficiary of
                  the Vendor’s services. For example:
                </p>
                <p>
                  <ul>
                    <li>
                      If your parent entered into a contract with a Vendor to book a venue for your
                      wedding, that parent and both you and your spouse are considered “clients” of
                      the venue hereunder and eligible to leave a Vendor Review.
                    </li>
                    <li>
                      If your best friend booked a DJ for your bachelorette, you and your best
                      friend are each considered “clients” of the DJ hereunder and are eligible to
                      leave a Vendor Review.
                    </li>
                    <li>
                      If your spouse entered into a contract with the florist for your wedding but
                      you didn’t sign that contract, both you and your future spouse are considered
                      “clients” of the florist hereunder and are eligible to leave a Vendor Review.
                    </li>
                  </ul>
                </p>
              </li>
              <li>
                <p>
                  <strong>Fellow Wedding Professionals:</strong> You are a fellow wedding
                  professional of the Vendor and serviced the same client for the same event, and
                  such event occurred within four (4) years prior to the date on which you are
                  submitting the Vendor Review.
                </p>
              </li>
              <li>
                <p>
                  <strong>Vendor Responses:</strong> You are a Vendor leaving a response to a Vendor
                  Review from a client or a fellow wedding professional in accordance with these
                  Vendor Review Terms and Conditions.
                </p>
              </li>
            </ul>
          </p>
          <p>
            If you are a Vendor and would like to respond to a Vendor Review about your products or
            services, you may submit your response to Vendor_Reviews@zola.com or via email to your
            Vendor Experience representative and such response will generally be posted to the
            Website within three (3) business days. Any such response must (i) be limited to 1,000
            characters; (ii) include the name of the user who posted the applicable Vendor Review
            and the text of their review; and (iii) include your first and last name and, if
            applicable, your title and the name of the Vendor business on behalf of which you are
            submitting the response. No more than one (1) response from a Vendor is permitted per
            Vendor Review, and users are not permitted to submit responses to Vendor’s responses.
            Zola reserves the right not to post any response that violates these Vendor Review Terms
            and Conditions, our <ZolaTermsLink />, or applicable Regulation, in each case as
            determined by Zola in its sole discretion.
          </p>
          <p>
            As our Website is a neutral venue, we cannot, do not, and will not make any comments on
            behalf of any user (other than to post a response on behalf of a Vendor, as described
            above). Vendors may contact us to initiate a dispute regarding a Vendor Review by
            emailing Vendor_Reviews@zola.com with (i) the name of the user who posted the applicable
            Vendor Review and the text of their review, (ii) your first and last name and, if
            applicable, your title and the name of the Vendor business on behalf of which you are
            submitting such Vendor Review; (iii) a reasonably detailed description of your reason
            for disputing the Vendor Review and (iv) any supporting documentation or evidence you
            may have. If we decide, in our sole discretion, that it is appropriate to reach out to
            the user who submitted the original Vendor Review to request additional information,
            such user must reply within seven (7) days of receiving contact from Zola for their
            reply to be considered in the dispute resolution process. The decision to remove a
            Vendor Review will be taken by Zola in its sole discretion, and all decisions are final
            unless otherwise determined by Zola in its sole discretion. We will only remove Vendor
            Reviews submitted within the last twelve (12) months; all other Vendor Reviews are
            ineligible for removal and dispute unless otherwise determined by Zola in its sole
            discretion. In order to protect the integrity of Vendor Reviews, we will only consider
            removing a Vendor Review (and we reserve the right to remove any Vendor Review) that:
          </p>
          <p>
            <ul>
              <li>
                contains profane, vulgar, libelous, harassing, abusive, obscene or sexually explicit
                material, or is inappropriate or discriminatory with respect to race, gender,
                sexuality, ethnicity or other intrinsic characteristic;
              </li>
              <li>
                contains confidential or personal information, such as a personal phone number, date
                of birth or physical address (outside of the business address);
              </li>
              <li>
                is for a non-wedding related event or otherwise is unrelated to the goods or
                services offered by the applicable Vendor;
              </li>
              <li>is materially factually inaccurate (as established by documentary evidence);</li>
              <li>
                is an advertisement, solicitation, promotional material, “junk mail,” “spam,” chain
                letters, pyramid scheme or any form of commercial content;
              </li>
              <li>
                with respect to which we are provided with a binding ruling or settlement agreement
                from a valid and certified dispute resolution service or a court order finding that
                the Vendor Review is slanderous, libelous, defamatory or otherwise illegal or the
                court order requires removal;
              </li>
              <li>
                makes any reference to actions taken or purported to be taken by Zola or any law
                enforcement organization;
              </li>
              <li>
                misrepresents or impersonates a user or is from a user that cannot be verified;
              </li>
              <li>
                violates these Vendor Review Terms and Conditions or our <ZolaTermsLink />; or
              </li>
              <li>
                violates applicable Regulation or where removal is required under applicable
                Regulation.
              </li>
            </ul>
          </p>
          <p>As a Vendor, you agree that you will not:</p>
          <p>
            <ul>
              <li>
                attempt to restrict in any way a user’s right to post a Vendor Review, by contract
                or otherwise;
              </li>
              <li>
                offer a user cash or other compensation in any form in exchange for a more favorable
                Vendor Review;
              </li>
              <li>
                attempt to gain Vendor Reviews by soliciting multiple Vendor Reviews from the same
                user; or
              </li>
              <li>
                post or attempt to post, in any manner or by any means, a Vendor Review on your own
                account.
              </li>
            </ul>
          </p>
          <p>
            You agree and acknowledge that Zola shall be entitled to retain all Vendor Reviews
            associated with you (including any of your responses). All Vendor Reviews are “User
            Submissions” under Zola’s <ZolaTermsLink /> and are therefore subject to all terms of
            our <ZolaTermsLink /> regarding User Submissions, including without limitation those set
            forth under the heading “Your Submissions” in such <ZolaTermsLink />. For the avoidance
            of doubt, Vendor Reviews are not Limited Audience User Submissions. Vendor Reviews about
            you or your business will not be your Content.
          </p>
          <p>
            By submitting a Vendor Review, you automatically grant to Zola a worldwide,
            royalty-free, perpetual, irrevocable, non-exclusive and fully transferable and
            sublicensable right and license to use, reproduce, modify, delete in its entirety,
            adapt, publish, edit, translate, distribute, perform, create derivative work of and
            display, sell and/or distribute such Vendor Reviews alone, or as a part of other works
            in any form, media, or technology, whether now known or hereafter developed, without any
            compensation to you. You also grant Zola permission to attribute such Vendor Review to
            you, your partner and/or your business, and to use your name, your partner’s name or
            your business’ name for such purpose, as applicable.
          </p>
          <p>
            Zola reserves the right to change, condense, withhold publication, remove or delete any
            Vendor Review on the Website that Zola deems, in its sole discretion, to violate these
            Vendor Review Terms and Conditions, our <ZolaTermsLink />, or applicable Regulation, in
            each case as determined by Zola in its sole discretion. Zola does not guarantee that you
            will have any recourse through Zola to edit or delete any Vendor Review that you have
            submitted. You acknowledge that you, not Zola, are responsible for the contents of your
            Vendor Review. None of the Vendor Reviews that you submit shall be subject to any
            obligation of confidence on the part of Zola, its subsidiaries, affiliates, successors,
            assigns and its and their respective directors, officers, agents, service providers,
            suppliers and employees. Vendor Reviews are not endorsed by Zola, and do not represent
            the views of Zola or of any affiliate or partner of Zola. Zola does not assume liability
            for any Vendor Reviews or for any claims, liabilities, or losses resulting from or
            relating to Vendor Reviews.
          </p>
        </Fragment>
      ),
    },
  ],
  lastUpdated: 'Last Updated: November 16, 2022',
};

export const NYC_VENDOR_REFERRAL_TERMS: StandardTermsAndConditions = {
  title: `NYC Invite-a-Wedding Venue – Referral Program Terms and Conditions`,
  sections: [
    {
      title: 'General',
      content: (
        <Fragment>
          These Referral Program Terms and Conditions (“Terms & Conditions”) apply to Zola’s
          Invite-a-Wedding Venue referral program (the “Program”). These Terms & Conditions are
          effective as of September 15, 2022. The Program is a promotional activity through which
          current Zola Vendors will have the opportunity to earn the referral reward described below
          (“Referral Reward”), subject to and in accordance with these Terms & Conditions. By
          inviting a wedding venue or otherwise participating in the Program, the referring Zola
          Vendor (“Referring Vendor”) and referred wedding venue invited by such Referring Vendor
          (“Referred Venue”) agree to be bound by these Terms & Conditions, <ZolaTermsLink />, and
          Zola’s
          <ZolaPrivacyLink />. Capitalized terms used but not defined in these Terms & Conditions
          have the meanings set forth in Zola’s <ZolaTermsLink />. Program ends December 31, 2022.
        </Fragment>
      ),
    },
    {
      title: 'Participation & Eligibility',
      content: `To participate in the Program, the Referring Vendor must (1) have a bona fide user account on Vendor Marketplace which is linked to publicly accessible, published Vendor page on Vendor Marketplace (a “Qualified Account”), and (2) invite a Referred Venue who is a Qualified New User to create a Qualified Account via an invite mechanism authorized by Zola, including but not limited to an email or personal referral link. A “Qualified New User” is an individual or entity (a) that qualifies as a wedding venue, (b) that does not currently have, and has never had, a Qualified Account, and (c) whose business is located in New York, NY, in each case as determined by Zola in its sole discretion.

            The Referring Vendor will receive a Referral Reward only if the Referred Venue is a Qualified New User who signs up for and creates a Qualified Account as a result of an invite from the Referring Vendor via a Zola-authorized invite mechanism. If the Referred Venue does not follow the directions in the invite email, personal referral link, or other valid invite mechanism to accept the invitation, the Referring Vendor may not receive the Referral Reward, and Zola will have no liability to the Referring Vendor. The Referring Vendor and the Referred Venue cannot be the same individual or entity (for example, by using a different email address).`,
    },
    {
      title: 'Awards & Redemption',
      content: `If a Referred Venue is a Qualified New User and creates a Qualified Account via an
              invite mechanism authorized by Zola, the associated Referring Vendor will earn a “Referral Reward”
              of $300.00 USD. Zola reserves the right to change the amount or type of Referral Reward being offered at any time.
              Zola will deliver the Referral Reward to the Referring Vendor within 15 days following the end of the calendar month
              in which the Referring Vendor earns the Referral Reward, provided both the Referring Vendor and the Referred Venue are in
              compliance with these Terms & Conditions. The Referring Vendor will receive only one Referral Reward per Referred Venue. If requested by Zola, The Referring Vendor shall submit, and delivery of the Referral Reward shall be subject to, a complete and accurate W9 form and/or such additional documentation or information as is required by Zola.`,
    },
    {
      title: 'Additional Terms & Conditions',
      content: `The Program is void where prohibited by law. Zola reserves the right at any time to modify or discontinue the Program, either temporarily or permanently (or any part thereof), without any liability to you. Taxes are not included. Should there be any tax liability for the accumulation and/or use of a Referral Reward, such taxes are the sole responsibility of the participant. As a Referring Vendor, you agree that sending invites to alternate email addresses or accounts or otherwise attempting to circumvent the Program is a violation of these Terms & Conditions and may result in forfeiture of the Referral Reward and/or your Zola account. Zola reserves the right to void referrals and Referral Rewards earned if we suspect that the referrals or Referral Rewards were earned in a fraudulent manner, in a manner that violates these Terms & Conditions or in a manner otherwise not intended by Zola. Referring Vendors must not conduct their own promotion in connection with the Program. You may not engage in any promotional, marketing, or other advertising activities on behalf of Zola, including by using any trademarks of Zola. These Terms & Conditions are subject to change and Zola reserves the right to amend them at any time by posting updated versions. Referring Vendors’ Vendor accounts must be active and in good standing, as determined by Zola in its sole discretion, to receive a Referral Reward. Determination of Program eligibility and qualifying referrals, as well as any exceptions, is at the sole discretion of Zola.`,
    },
  ],

  lastUpdated: 'Last Updated: September 19, 2022',
};

export const MARCH_2023_VENDOR_REFERRAL_TERMS: StandardTermsAndConditions = {
  title: 'Zola’s March 2023 Invite-a-Wedding Vendor Program – Program Terms and Conditions',
  sections: [
    {
      title: 'General',
      content: (
        <Fragment>
          These Referral Program Terms and Conditions apply to Zola’s March 2023 Invite-a-Wedding
          Vendor program (the “Program”). These Referral Program Terms and Conditions are effective
          from March 1, 2023 to March 31, 2023 (the “Program Period”). The Program is a promotional
          activity through which Zola Vendors will have the opportunity to earn the applicable
          referral reward described below (“Referral Reward”), subject to and in accordance with
          these Terms & Conditions. The referring Zola Vendor (“Referring Vendor”) and any referred
          wedding vendor invited by such Referring Vendor (each, a “Referred Vendor”) agree to be
          bound by these Referral Program Terms and Conditions, Zola’s <ZolaTermsLink />, and Zola’s{' '}
          <ZolaPrivacyLink />. Capitalized terms used but not defined in these Terms and Conditions
          have the meanings set forth in Zola’s <ZolaTermsLink />.
        </Fragment>
      ),
    },
    {
      title: 'Participation & Eligibility',
      content: (
        <Fragment>
          <p>
            To participate in the Program and be eligible for a Referral Reward, a Referring Vendor
            must: (1) have a bona fide user account on Vendor Marketplace, excluding, for the
            avoidance of doubt, accounts created by couples on the Website, “guest” accounts or
            log-ins created in connection with the purchase of registry gifts (a “Qualified
            Account”0, (2) have a publicly accessible, published Vendor page on Vendor Marketplace
            linked to such Qualified Account (“Published Vendor Profile”), and (3) during the
            Program Period, invite Referred Vendors, each of whom is an individual or entity that
            has not ever had a Qualified Account (a “Qualified New User”), to create a Qualified
            Account via an invite mechanism authorized by Zola, including but not limited to an
            email or personal referral link (“Authorized Invite”), three of which Referred Vendors
            must become a Qualified Referred Vendor (defined below).
          </p>
          <p>
            To participate in the Program, become a “Qualified Referred Vendor,” and be eligible for
            a Referral Reward, a Referred Vendor must (1) be a Qualified New User, (2) create a
            Qualified Account during the Program Period in direct response to an Authorized Invite,
            and comply with any instructions set forth in such Authorized Invite, and (3) have a
            Published Vendor Profile by the end of the Program Period (any such Referred Vendor, a
            “Qualified Referred Vendor”). A Referring Vendor and a Referred Vendor cannot be the
            same individual or entity (for example, by using a different email address).
          </p>
        </Fragment>
      ),
    },
    {
      title: 'Awards & Redemption',
      content: (
        <Fragment>
          <p>
            A Referring Vendor will receive one (1) Referral Reward for every three (3) Qualified
            Referred Vendors referred by it during the Program Period. For Referring Vendors, the
            “Referral Reward” is the number of Vendor Credits required for three (3) Connections (as
            defined in our <ZolaVendorCreditsTermsLink />) as of April 1, 2023, with the exact
            number of Vendor Credits being determined according to the Referred Vendor’s category.
          </p>
          <p>
            Any Referred Vendor that becomes a Qualified Referred Vendor during the Program Period
            will receive one (1) Referral Reward. For a Qualified Referred Vendor, the “Referral
            Reward” is the amount equal to the number of Vendor Credits issued by Zola to newly
            published Vendors in such Qualified Referred Vendor’s category as of April 1, 2023.
          </p>
          <p>
            Zola reserves the right to change the amount or type of Referral Reward being offered at
            any time. Zola will deliver the Referral Reward by issuing the applicable Vendor Credits
            to each Vendor’s Qualified Account as follows: (i) for Referred Vendors, Zola will
            deliver the Referral Reward approximately three (3) to six (6) weeks after such Vendor
            becomes a Qualified Referred Vendor, and (ii) for Referring Vendors, Zola will deliver
            the Referral Reward approximately three (3) to six (6) weeks after the last of a group
            of three (3) of such Referring Vendor’s Referred Vendors become Qualified Referred
            Vendors.
          </p>
        </Fragment>
      ),
    },
    {
      title: 'Additional Terms & Conditions',
      content: (
        <Fragment>
          The Program is void where prohibited by law. Zola reserves the right at any time to modify
          or discontinue the Program, either temporarily or permanently (or any part thereof),
          without any liability to you. Taxes are not included. Should there be any tax liability
          for the accumulation and/or use of a Referral Reward, such taxes are the sole
          responsibility of the participant. As a Referring Vendor, you agree that sending invites
          to alternate email addresses or accounts or otherwise attempting to circumvent the Program
          is a violation of these Referral Program Terms and Conditions and may result in forfeiture
          of the Referral Reward and/or your Zola account. Zola reserves the right to void referrals
          and Referral Rewards earned if we suspect that the referrals or Referral Rewards were
          earned in a fraudulent manner, in a manner that violates these Referral Program Terms and
          Conditions or in a manner otherwise not intended by Zola. Referring Vendors and Referred
          Vendors must not conduct their own promotion in connection with the Program. You may not
          engage in any promotional, marketing, or other advertising activities on behalf of Zola,
          including by using any trademarks of Zola. These Referral Program Terms and Conditions are
          subject to change and Zola reserves the right to amend them at any time by posting updated
          versions. Referring Vendors’ and Referred Vendors’ Vendor accounts must be active and in
          good standing, as determined by Zola in its sole discretion, to receive a Referral Reward.
          Determination of Program eligibility and qualifying referrals, as well as any exceptions,
          are at the sole discretion of Zola.
        </Fragment>
      ),
    },
  ],
  lastUpdated: 'Last Updated: March 1, 2023',
};
